import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, Search, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { adminApi, formatApiError, refreshSiteSettings } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUSES = ["New", "Contacted", "Qualified", "Proposal Sent", "Won", "Lost"];
const SERVICE_FILTERS = [
  "Website Development", "AI Voice Agent", "WhatsApp AI Agent", "Website Chatbot",
  "Instagram AI Agent", "Facebook AI Agent", "CRM Integration", "Complete Automation Solution", "Other",
];
const STATUS_COLORS = {
  New: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  Contacted: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  Qualified: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  "Proposal Sent": "bg-violet-500/15 text-violet-300 border-violet-500/40",
  Won: "bg-green-500/15 text-green-300 border-green-500/40",
  Lost: "bg-red-500/15 text-red-300 border-red-500/40",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [service, setService] = useState("");
  const [openId, setOpenId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (service) params.service = service;
      const [s, e] = await Promise.all([
        adminApi().get("/admin/stats"),
        adminApi().get("/admin/enquiries", { params }),
      ]);
      setStats(s.data);
      setEnquiries(e.data.enquiries);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("rdp_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [q, status, service, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("rdp_admin_token")) {
      navigate("/admin/login");
      return;
    }
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, navigate]);

  useEffect(() => {
    if (tab === "settings" && !settings) {
      adminApi().get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    }
  }, [tab, settings]);

  const updateEnquiry = async (id, patch) => {
    try {
      const { data } = await adminApi().patch(`/admin/enquiries/${id}`, patch);
      setEnquiries((list) => list.map((e) => (e.enquiry_id === id ? data : e)));
      toast.success("Enquiry updated.");
      const s = await adminApi().get("/admin/stats");
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await adminApi().get("/admin/enquiries/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ridgeline-enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await adminApi().put("/admin/settings", settings);
      refreshSiteSettings();
      toast.success("Settings saved. They are now live on the website.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("rdp_admin_token");
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard-container" className="min-h-screen bg-[#030712]">
      <header className="sticky top-0 z-40 bg-slate-950/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-2">
            <button onClick={() => setTab("enquiries")} data-testid="admin-tab-enquiries"
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${tab === "enquiries" ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}>
              Enquiries
            </button>
            <button onClick={() => setTab("settings")} data-testid="admin-tab-settings"
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${tab === "settings" ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}>
              Site Settings
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" aria-label="Log out"
              className="ml-2 rounded-full border border-white/10 p-2 text-slate-400 hover:text-white transition-colors">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {tab === "enquiries" && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
              {[
                { label: "Total Enquiries", value: stats?.total },
                ...["New", "Contacted", "Qualified", "Won"].map((s) => ({ label: s, value: stats?.by_status?.[s] })),
              ].map((c) => (
                <div key={c.label} className="glass-card rounded-xl px-5 py-4">
                  <p className="font-mono2 text-[10px] tracking-[0.2em] text-slate-500">{c.label.toUpperCase()}</p>
                  <p className="mt-1.5 font-display text-2xl font-bold text-slate-50">{c.value ?? "–"}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 mb-5">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, company, email, mobile, reference..."
                  className="w-full rounded-lg bg-slate-900/70 border border-white/10 pl-10 pr-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-blue-500" />
              </div>
              <select data-testid="admin-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
                className="rounded-lg bg-slate-900/70 border border-white/10 px-4 py-2.5 text-sm text-slate-200 outline-none focus:border-blue-500">
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="admin-service-filter" value={service} onChange={(e) => setService(e.target.value)}
                className="rounded-lg bg-slate-900/70 border border-white/10 px-4 py-2.5 text-sm text-slate-200 outline-none focus:border-blue-500">
                <option value="">All services</option>
                {SERVICE_FILTERS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={exportCsv} data-testid="admin-export-csv-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-blue-500/50 px-4 py-2.5 text-sm font-semibold text-blue-300 transition-colors hover:bg-blue-600 hover:text-white">
                <Download size={15} /> Export CSV
              </button>
            </div>

            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table data-testid="admin-lead-table" className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-left">
                      {["Reference", "Customer", "Contact", "Services", "Status", "Date", ""].map((h) => (
                        <th key={h} className="px-5 py-3.5 font-mono2 text-[10px] tracking-[0.2em] text-slate-500">{h.toUpperCase()}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">
                        <Loader2 className="inline animate-spin mr-2" size={16} />Loading enquiries...
                      </td></tr>
                    )}
                    {!loading && enquiries.length === 0 && (
                      <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No enquiries found.</td></tr>
                    )}
                    {!loading && enquiries.map((e) => (
                      <EnquiryRow key={e.enquiry_id} e={e} open={openId === e.enquiry_id}
                        onToggle={() => setOpenId(openId === e.enquiry_id ? null : e.enquiry_id)}
                        onUpdate={updateEnquiry} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "settings" && settings && (
          <div className="max-w-2xl">
            <h2 className="font-display text-xl font-bold text-slate-50 mb-2">Site Settings</h2>
            <p className="text-sm text-slate-400 mb-6">
              These settings power the floating WhatsApp button, contact page details, and enquiry email notifications.
            </p>
            <div className="space-y-4">
              {[
                ["whatsapp_number", "WhatsApp Number (with country code, digits only)", "e.g. 919876543210"],
                ["whatsapp_message", "WhatsApp Pre-filled Message", ""],
                ["business_email", "Business Email (shown on site)", ""],
                ["business_phone", "Business Phone (shown on site)", ""],
                ["business_address", "Business Address (shown on site)", ""],
                ["business_hours", "Business Hours (shown on site)", "e.g. Mon–Sat, 9:30 AM – 6:30 PM"],
                ["notification_email", "Enquiry Notification Email (receives new lead alerts)", ""],
              ].map(([key, label, ph]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5" htmlFor={`set-${key}`}>{label}</label>
                  <input id={`set-${key}`} data-testid={`settings-${key.replace(/_/g, "-")}-input`}
                    value={settings[key] || ""} placeholder={ph}
                    onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                    className="w-full rounded-lg bg-slate-900/70 border border-white/10 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-600 outline-none focus:border-blue-500" />
                </div>
              ))}
              <button onClick={saveSettings} data-testid="settings-save-btn" disabled={savingSettings}
                className="rounded-full bg-blue-600 px-7 py-3 text-sm font-semibold text-white transition-all hover:bg-blue-500 disabled:opacity-60 flex items-center gap-2">
                {savingSettings && <Loader2 className="animate-spin" size={15} />}
                Save Settings
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function EnquiryRow({ e, open, onToggle, onUpdate }) {
  const [notes, setNotes] = useState(e.notes || "");
  const [assigned, setAssigned] = useState(e.assigned_to || "");
  const [followUp, setFollowUp] = useState(e.follow_up_date || "");

  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors" onClick={onToggle}>
        <td className="px-5 py-3.5 font-mono2 text-xs text-blue-400 whitespace-nowrap">{e.reference}</td>
        <td className="px-5 py-3.5">
          <p className="font-semibold text-slate-100">{e.full_name}</p>
          <p className="text-xs text-slate-500">{e.company_name}</p>
        </td>
        <td className="px-5 py-3.5 text-xs text-slate-300">
          <p>{e.mobile}</p>
          <p className="text-slate-500">{e.email}</p>
        </td>
        <td className="px-5 py-3.5 text-xs text-slate-400 max-w-[180px]">{e.services_text}</td>
        <td className="px-5 py-3.5" onClick={(ev) => ev.stopPropagation()}>
          <select value={e.status} onChange={(ev) => onUpdate(e.enquiry_id, { status: ev.target.value })}
            data-testid={`status-select-${e.reference}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold outline-none bg-slate-900 ${STATUS_COLORS[e.status] || ""}`}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </td>
        <td className="px-5 py-3.5 text-xs text-slate-500 whitespace-nowrap">
          {e.created_at ? new Date(e.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
        </td>
        <td className="px-5 py-3.5 text-slate-500">{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</td>
      </tr>
      {open && (
        <tr className="border-b border-white/5 bg-slate-900/40">
          <td colSpan={7} className="px-5 py-5" onClick={(ev) => ev.stopPropagation()}>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <p className="font-mono2 text-[10px] tracking-[0.2em] text-slate-500 mb-2">REQUIREMENTS</p>
                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{e.requirements}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-slate-400">
                  <span>Industry: <b className="text-slate-200">{e.industry || "-"}</b></span>
                  <span>City: <b className="text-slate-200">{e.city || "-"}</b></span>
                  <span>Budget: <b className="text-slate-200">{e.budget || "-"}</b></span>
                  <span>Timeline: <b className="text-slate-200">{e.timeline || "-"}</b></span>
                  <span>Preferred contact: <b className="text-slate-200">{e.preferred_contact}</b></span>
                  <span>Source: <b className="text-slate-200">{e.source_page}</b></span>
                  {e.website_url && <span className="col-span-2">Website: <b className="text-slate-200">{e.website_url}</b></span>}
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block font-mono2 text-[10px] tracking-[0.2em] text-slate-500 mb-1.5">INTERNAL NOTES</label>
                  <textarea rows={3} value={notes} onChange={(ev) => setNotes(ev.target.value)}
                    data-testid={`notes-input-${e.reference}`}
                    className="w-full rounded-lg bg-slate-900/70 border border-white/10 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-mono2 text-[10px] tracking-[0.2em] text-slate-500 mb-1.5">ASSIGNED TO</label>
                    <input value={assigned} onChange={(ev) => setAssigned(ev.target.value)} placeholder="Sales rep"
                      className="w-full rounded-lg bg-slate-900/70 border border-white/10 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="block font-mono2 text-[10px] tracking-[0.2em] text-slate-500 mb-1.5">FOLLOW-UP DATE</label>
                    <input type="date" value={followUp} onChange={(ev) => setFollowUp(ev.target.value)}
                      className="w-full rounded-lg bg-slate-900/70 border border-white/10 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500 [color-scheme:dark]" />
                  </div>
                </div>
                <button
                  onClick={() => onUpdate(e.enquiry_id, { notes, assigned_to: assigned, follow_up_date: followUp })}
                  data-testid={`save-details-${e.reference}`}
                  className="rounded-full bg-blue-600 px-5 py-2 text-xs font-semibold text-white hover:bg-blue-500 transition-colors">
                  Save Details
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
